import { Pause, Play } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useAudioPlayer } from '../../../lib/use-audio-player';
import { docSource } from '../../../lib/doc-viewer';
import { Card, IconButton, Typography } from '../ui';
import { DocNoticeState } from './DocViewerStates';
import styles from './DocViewerModal.module.css';

// An audio artifact — a voice note, a recording, an `.mp3` an agent produced. Before this, an `audio/*` file
// only had a player INSIDE a chat bubble (`AudioAttachmentPlayer`); the same file opened from the Files
// gallery had no player at all and fell through to a download.
//
// The playback behaviour is the shared `useAudioPlayer` hook, so the one-at-a-time lock spans both surfaces:
// starting this pauses a voice note playing in chat. Only the chrome differs — a reading surface gets a
// centred player with a real progress track, not a 240px pill sized for a message bubble.
//
// Like image/video, the source is resolved straight from `docSource` and this view owns its own unavailable
// state: there is no such thing as inline audio bytes on web.
export function AudioDocView({ content, fileName }: { content?: string | null; fileName: string }) {
  const { t } = useTranslation();
  const source = docSource(content);
  const url = source?.kind === 'remote' ? source.url : null;
  const player = useAudioPlayer();

  if (!url) return <DocNoticeState message={t('docViewer.audioUnavailable')} />;

  return (
    <div className={styles.audioBody}>
      <audio {...player.audioProps} src={url} className={styles.audioElement} />
      {/* `<Card variant="outline" size="sm">` rather than a hand-rolled surface — it supplies the hairline,
          the radius and the padding, and the class carries only the layout (see CLAUDE.md "Cards"). */}
      <Card variant="outline" size="sm" className={styles.audioPlayer} role="group" aria-label={fileName}>
        <IconButton
          size="large"
          aria-label={player.playing ? t('common.stop') : t('docViewer.audioPlay')}
          onClick={player.toggle}
        >
          {player.playing ? <Pause size={20} aria-hidden /> : <Play size={20} aria-hidden />}
        </IconButton>
        {/* `aria-hidden`: the track is a pointer affordance. The button above carries the accessible name,
            and the elapsed/total time is announced as text beside it. */}
        <div className={styles.audioTrack} onClick={player.seek} aria-hidden>
          <div className={styles.audioRail} />
          <div className={styles.audioFill} style={{ width: `${Math.round(player.progress * 100)}%` }} />
        </div>
        <Typography variant="p3" color="secondary" component="span" className={styles.audioTime}>
          {player.timeLabel}
        </Typography>
      </Card>
    </div>
  );
}
